import React, { useState } from 'react';
import { X, Play, Clock, Info } from 'lucide-react';
import { VideoPlayer } from './VideoPlayer';
interface VideoSolutionsModalProps {
  videos: {
    id: number;
    title: string;
    duration: string;
    thumbnail?: string;
    description?: string;
  }[];
  title: string;
  onClose: () => void;
}
export function VideoSolutionsModal({
  videos,
  title,
  onClose
}: VideoSolutionsModalProps) {
  const [selectedVideo, setSelectedVideo] = useState(videos[0]);
  // Function to get subject-specific placeholder image
  const getSubjectPlaceholder = () => {
    // Based on title, determine which subject it might be
    if (title.toLowerCase().includes('matematik')) {
      return 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8bWF0aCUyMHByb2JsZW18ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=800&q=60';
    } else if (title.toLowerCase().includes('türkçe')) {
      return 'https://images.unsplash.com/photo-1457369804613-52c61a468e7d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8Ym9va3MlMjBhbmQlMjBwZW58ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=800&q=60';
    } else if (title.toLowerCase().includes('fen')) {
      return 'https://images.unsplash.com/photo-1532094349884-543bc11b234d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8c2NpZW5jZXxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60';
    } else if (title.toLowerCase().includes('inkılap') || title.toLowerCase().includes('tarih')) {
      return 'https://images.unsplash.com/photo-1447069387593-a5de0862481e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8NHx8aGlzdG9yeXxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60';
    } else if (title.toLowerCase().includes('ingilizce')) {
      return 'https://images.unsplash.com/photo-1546410531-bb4caa6b424d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8Mnx8ZW5nbGlzaHxlbnwwfHwwfHx8MA%3D%3D&auto=format&fit=crop&w=800&q=60';
    } else if (title.toLowerCase().includes('din')) {
      return 'https://images.unsplash.com/photo-1585036156171-384164a8c675?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8cmVsaWdpb258ZW58MHx8MHx8fDA%3D&auto=format&fit=crop&w=800&q=60';
    } else {
      return 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxzZWFyY2h8M3x8ZWR1Y2F0aW9ufGVufDB8fDB8fHww&auto=format&fit=crop&w=800&q=60';
    }
  };
  return <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50" data-id="element-1352">
      <div className="relative p-[1px] rounded-xl overflow-hidden bg-gradient-to-r from-purple-600 to-indigo-600 w-full max-w-4xl" data-id="element-1353">
        <div className="relative bg-black/90 rounded-xl p-6" data-id="element-1354">
          <div className="flex justify-between items-center mb-4" data-id="element-1355">
            <h2 className="text-xl font-bold" data-id="element-1356">{title}</h2>
            <button onClick={onClose} className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors" data-id="element-1357">
              <X className="w-5 h-5" data-id="element-1358" />
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-1359">
            {/* Video Player (2/3 width on desktop) */}
            <div className="md:col-span-2 space-y-4" data-id="element-1360">
              <VideoPlayer videoUrl="https://www.youtube.com/embed/dQw4w9WgXcQ" thumbnailUrl={selectedVideo.thumbnail || getSubjectPlaceholder()} enableFullscreen={true} data-id="element-1361" />
              <div data-id="element-1362">
                <h3 className="text-lg font-medium mb-2" data-id="element-1363">
                  {selectedVideo.title}
                </h3>
                <p className="text-gray-400 text-sm" data-id="element-1364">
                  {selectedVideo.description || 'Bu videoda konu ile ilgili soru çözümleri bulunmaktadır. Çözümlü sorular ile konuyu pekiştirebilirsiniz.'}
                </p>
              </div>
            </div>
            {/* Video List (1/3 width on desktop) */}
            <div data-id="element-1365">
              <h3 className="text-lg font-medium mb-3" data-id="element-1366">Videolar</h3>
              <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-2" data-id="element-1367">
                {videos.map(video => <div key={video.id} onClick={() => setSelectedVideo(video)} className={`flex gap-3 p-3 rounded-lg transition-colors cursor-pointer ${selectedVideo.id === video.id ? 'bg-white/15 ring-1 ring-purple-500' : 'bg-white/5 hover:bg-white/10'}`} data-id="element-1368">
                    <div className="relative w-20 h-12 rounded-lg overflow-hidden flex-shrink-0" data-id="element-1369">
                      <img src={video.thumbnail || getSubjectPlaceholder()} alt={video.title} className="w-full h-full object-cover" data-id="element-1370" />
                      <div className="absolute inset-0 flex items-center justify-center" data-id="element-1371">
                        <div className="p-1 rounded-full bg-black/50" data-id="element-1372">
                          <Play className="w-3 h-3" fill="white" data-id="element-1373" />
                        </div>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-1374">
                      <h4 className="font-medium text-sm line-clamp-2" data-id="element-1375">
                        {video.title}
                      </h4>
                      <div className="flex items-center gap-2 mt-1 text-xs text-gray-400" data-id="element-1376">
                        <Clock className="w-3 h-3" data-id="element-1377" />
                        <span data-id="element-1378">{video.duration}</span>
                      </div>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}